import { ChevronDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';
import { endOfMonth, formatDuration, startOfMonth } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ClockArt, TasksArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ExportMenu } from '@/components/export-menu';
import { downloadCsv } from '@/lib/csv';
import { downloadXlsx } from '@/lib/xlsx';
import { UtilisationTable } from '@/components/reporting/utilisation-table';
import { useTeamOverview, type TeamOverview } from '@/lib/team-api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePeriodShortcuts } from '@/lib/shortcuts';

/**
 * The team over one month, from three angles.
 *
 * People, projects and days-still-owed are one screen rather than three because
 * they are one question asked three ways - and because they are computed from the
 * same five queries, so three screens could disagree about which days are owed.
 */
const TABS = ['people', 'projects', 'utilisation', 'missing'] as const;

export function TeamPage() {
  // The tab is part of the path so each view can be linked to from the sidebar
  // and bookmarked. "Missing days" in particular is a screen somebody opens
  // directly, not one they navigate to through two other tabs.
  const { tab } = useParams();

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setMonth(shiftMonth(month, -1)),
    next: () => setMonth(shiftMonth(month, 1)),
    today: () => setMonth(orgMonth()),
  });
  const navigate = useNavigate();
  const activeTab = TABS.includes(tab as (typeof TABS)[number]) ? tab! : 'people';

  // The month is in the URL (?month=2026-09) so a refresh, a bookmark or a link
  // sent to somebody opens the same month instead of falling back to this one.
  const [params, setParams] = useSearchParams();
  const asked = params.get('month');
  const month = asked && /^\d{4}-(0[1-9]|1[0-2])$/.test(asked) ? asked : orgMonth();
  const setMonth = (next: string) =>
    setParams(next === orgMonth() ? {} : { month: next }, { replace: true });
  const from = startOfMonth(`${month}-01`);
  const to = endOfMonth(`${month}-01`);
  const { data, isPending, isError, error } = useTeamOverview(from, to);

  return (
    <PageContainer>
      <PageHeader
        title="Team"
        description="Where the month's hours went, and which days are still empty."
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous month" title="Previous month (← or j)"
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="ts-num min-w-[9rem] text-center text-sm font-semibold">
          {monthLabel(month)}
        </span>
        <Button
          variant="outline"
          size="icon"
          aria-label="Next month" title="Next month (→ or k)"
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
          This month
        </Button>
      </div>

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load the team for that month.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <Tabs
          value={activeTab}
          onValueChange={(next) => navigate(`/reporting/team/${next}${window.location.search}`, { replace: true })}
        >
          <TabsList>
            <TabsTrigger value="people">People</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            {/* Beside people and projects rather than on its own page: it answers
                the same question about the same month, one level up. */}
            <TabsTrigger value="utilisation">Utilisation</TabsTrigger>
            <TabsTrigger value="missing">
              Days owed
              {countOwed(data) > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {countOwed(data)}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ------------------------------------------------------ people -- */}
          <TabsContent value="utilisation">
            <div className="mt-4">
              <UtilisationTable from={from} to={to} />
            </div>
          </TabsContent>

          <TabsContent value="people">
            <div className="mb-3 flex justify-end">
              <ExportMenu
                onCsv={() =>
                  downloadCsv(`team-people-${data.from.slice(0, 7)}.csv`, [
                    PEOPLE_COLUMNS.map((c) => c.header),
                    ...peopleRows(data),
                  ])
                }
                onXlsx={() =>
                  downloadXlsx(
                    `team-people-${data.from.slice(0, 7)}.xlsx`,
                    'People',
                    PEOPLE_COLUMNS,
                    peopleRows(data),
                  )
                }
              />
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="min-w-[620px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Person</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        {/* Hours alone hides the shape of a month: 40 hours over two
                            days is not the same month as 40 over ten. */}
                        <TableHead className="text-right">Days logged</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.people.map((person) => (
                        <ExpandableRow
                          key={person.id}
                          label={person.left ? `${person.name} (left)` : person.name}
                          seconds={person.seconds}
                          extra={person.daysLogged}
                          breakdown={person.projects}
                          emptyNote="No time recorded this month."
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------------------------------------------------- projects -- */}
          <TabsContent value="projects">
            <div className="mb-3 flex justify-end">
              <ExportMenu
                onCsv={() =>
                  downloadCsv(`team-projects-${data.from.slice(0, 7)}.csv`, [
                    PROJECT_COLUMNS.map((c) => c.header),
                    ...projectRows(data),
                  ])
                }
                onXlsx={() =>
                  downloadXlsx(
                    `team-projects-${data.from.slice(0, 7)}.xlsx`,
                    'Projects',
                    PROJECT_COLUMNS,
                    projectRows(data),
                  )
                }
              />
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="min-w-[620px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Project</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">People</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.projects.map((project) => (
                        <ExpandableRow
                          key={project.id}
                          label={project.name}
                          sublabel={project.customer}
                          seconds={project.seconds}
                          extra={project.people.length}
                          breakdown={project.people}
                        />
                      ))}
                      {data.projects.length === 0 && <EmptyRow art={ClockArt}>No time recorded this month</EmptyRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ----------------------------------------------------- missing -- */}
          <TabsContent value="missing" className="space-y-3">
            <div className="mb-3 flex justify-end">
              <ExportMenu
                onCsv={() =>
                  downloadCsv(`missing-days-${data.from.slice(0, 7)}.csv`, [
                    MISSING_COLUMNS.map((c) => c.header),
                    ...missingRows(data),
                  ])
                }
                onXlsx={() =>
                  downloadXlsx(
                    `missing-days-${data.from.slice(0, 7)}.xlsx`,
                    'Missing days',
                    MISSING_COLUMNS,
                    missingRows(data),
                  )
                }
              />
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Working days up to {shortDate(data.owedTo)} with nothing recorded. Weekends, the
              person's own contracted days off, mandatory public holidays and approved or
              requested leave are all excluded — and days that have not happened yet are not
              counted as owed.
            </p>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="min-w-[620px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Person</TableHead>
                        <TableHead className="w-20 text-right">Days</TableHead>
                        <TableHead>Which days</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {owing(data).map((person) => (
                          <TableRow key={person.id}>
                            <TableCell className="whitespace-nowrap font-medium">
                              {person.name}
                            </TableCell>
                            <TableCell className="ts-num text-right">
                              {person.missingDates.length}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {person.missingDates.map((day) => (
                                  <Badge key={day} variant="outline" className="ts-num font-normal">
                                    {shortDate(day)}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      {countOwed(data) === 0 && (
                        <EmptyRow art={TasksArt}>Everybody is up to date — nothing is owed</EmptyRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Said rather than left as a silent gap in the list above: somebody with
                no contract has no statement of which days they work, so no day can be
                called owed for them. That is a setup gap, not a clean record. */}
            {data.people.some((p) => !p.hasContract) && (
              <Alert>
                <AlertDescription className="text-sm leading-relaxed">
                  {data.people.filter((p) => !p.hasContract).length} of{' '}
                  {data.people.length} people have no working contract, so no days can be
                  counted as owed for them:{' '}
                  <span className="font-medium">
                    {data.people
                      .filter((p) => !p.hasContract)
                      .map((p) => p.name)
                      .join(', ')}
                  </span>
                  .
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      )}
    </PageContainer>
  );
}

/**
 * A total that opens to show what it is made of.
 *
 * The breakdown is the point of both tables - a person's 160 hours means something
 * different spread over one project than over six - but showing every child inline
 * makes an 80-person table unreadable. So the total leads and the parts are one
 * click away.
 */
function ExpandableRow({
  label,
  sublabel,
  seconds,
  extra,
  breakdown,
  emptyNote,
}: {
  label: string;
  sublabel?: string;
  seconds: number;
  extra: number;
  breakdown: { name: string; seconds: number }[];
  emptyNote?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const canOpen = breakdown.length > 0;

  return (
    <>
      <TableRow
        className={canOpen ? 'cursor-pointer' : ''}
        onClick={canOpen ? () => setOpen((v) => !v) : undefined}
      >
        <TableCell>
          <div className="flex items-center gap-1.5">
            {canOpen ? (
              <ChevronDown
                className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${
                  open ? '' : '-rotate-90'
                }`}
              />
            ) : (
              <span className="size-3.5 shrink-0" />
            )}
            <div>
              <div className="font-medium">{label}</div>
              {sublabel && <div className="text-xs text-muted-foreground">{sublabel}</div>}
            </div>
          </div>
        </TableCell>
        <TableCell className="ts-num whitespace-nowrap text-right font-medium">
          {seconds === 0 ? (
            <span className="font-normal text-muted-foreground">{emptyNote ?? '0:00'}</span>
          ) : (
            formatDuration(seconds)
          )}
        </TableCell>
        <TableCell className="ts-num text-right text-muted-foreground">{extra || '—'}</TableCell>
      </TableRow>
      {open &&
        breakdown.map((child) => (
          <TableRow key={child.name} className="bg-muted/30">
            <TableCell className="pl-9 text-sm text-muted-foreground">{child.name}</TableCell>
            <TableCell className="ts-num whitespace-nowrap text-right text-sm">
              {formatDuration(child.seconds)}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
    </>
  );
}

function EmptyRow({ art, children }: { art: React.ComponentProps<typeof EmptyState>['art']; children: string }) {
  return (
    <TableRow>
      <TableCell colSpan={3}>
        <EmptyState size="sm" art={art} title={children} />
      </TableCell>
    </TableRow>
  );
}

/**
 * One file per view, each shaped like the table it came from.
 *
 * The breakdown rows are indented under their parent with a leading marker rather
 * than flattened into repeated parent names: the file is meant to be read as well
 * as pivoted, and a reader should be able to see the same structure the screen
 * showed. Hours go out as decimal AND as H:MM - the first is what a spreadsheet
 * can sum, the second is what a person recognises.
 */
const PEOPLE_COLUMNS = [
  { header: 'Person', width: 26 },
  { header: 'Project', width: 32 },
  { header: 'Hours', width: 12 },
  { header: 'Decimal hours', width: 15, numeric: true },
  { header: 'Days logged', width: 13, numeric: true },
];

/**
 * The rows are built once and handed to whichever writer was asked for, so the
 * CSV and the workbook cannot describe different data.
 *
 * The decimal column is a real NUMBER in the workbook, which is the whole reason
 * to offer Excel at all - a column of hours nobody can sum without re-typing it
 * is a screenshot with extra steps.
 */
function peopleRows(data: TeamOverview) {
  const rows: (string | number | null)[][] = [];
  for (const person of data.people) {
    rows.push([person.name, null, formatDuration(person.seconds), decimal(person.seconds), person.daysLogged]);
    for (const project of person.projects) {
      rows.push([null, `  ${project.name}`, formatDuration(project.seconds), decimal(project.seconds), null]);
    }
  }
  return rows;
}

const PROJECT_COLUMNS = [
  { header: 'Project', width: 32 },
  { header: 'Customer', width: 26 },
  { header: 'Person', width: 26 },
  { header: 'Hours', width: 12 },
  { header: 'Decimal hours', width: 15, numeric: true },
];

function projectRows(data: TeamOverview) {
  const rows: (string | number | null)[][] = [];
  for (const project of data.projects) {
    rows.push([project.name, project.customer, null, formatDuration(project.seconds), decimal(project.seconds)]);
    for (const person of project.people) {
      rows.push([null, null, `  ${person.name}`, formatDuration(person.seconds), decimal(person.seconds)]);
    }
  }
  return rows;
}

const MISSING_COLUMNS = [
  { header: 'Person', width: 26 },
  { header: 'Days', width: 8, numeric: true, format: '0' },
  { header: 'Which days', width: 120 },
];

/** Who owes days, most first - the order the screen shows and the export writes. */
const owing = (data: TeamOverview) =>
  data.people
    .filter((p) => p.missingDates.length > 0)
    .sort((a, b) => b.missingDates.length - a.missingDates.length);

/**
 * One row per PERSON, shaped exactly like the screen: the file is handed on as the
 * list of who owes what, and one row per day made it read nothing like the page.
 */
function missingRows(data: TeamOverview) {
  return owing(data).map((person) => [
    person.name,
    person.missingDates.length,
    person.missingDates.map(shortDate).join(', '),
  ]);
}

/** Hours a spreadsheet can add up, to two places. */
const decimal = (seconds: number) => (seconds / 3600).toFixed(2);

const countOwed = (data: TeamOverview) =>
  data.people.reduce((sum, p) => sum + p.missingDates.length, 0);

const UTC = { timeZone: 'UTC' } as const;

function shortDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...UTC,
  });
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    ...UTC,
  });
}

function shiftMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  const shifted = new Date(Date.UTC(year!, m! - 1 + delta, 1));
  return shifted.toISOString().slice(0, 7);
}
